import {Alert, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useReducer, useRef, useState} from 'react';

import {
  connectionRequiresUserLogin,
  ConnectionStatus,
  InstrumentServiceDetails,
} from 'instruments/connections/ConnectionConstants';
import {extractFileNames} from 'instruments/instrument-data/BrowseConnectionFilesModal';
import importInstrumentDataModalReducer, {
  ImportInstrumentDataModalActionType,
} from 'instruments/instrument-data/importInstrumentDataModalReducer';
import {
  INITIAL_STATE,
  InstrumentData,
  InstrumentQueryStatus,
  isUnselectableFileStatus,
  StepKey,
} from 'instruments/instrument-data/InstrumentDataConstants';
import {
  createBrowseFilesInstrumentQuery,
  pollInstrumentQueryStatus,
} from 'instruments/instrument-data/InstrumentDataDAO';
import InstrumentDataTable from 'instruments/instrument-data/InstrumentDataTable';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT,
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT,
} from 'performance/processLabAutomationEvents';
import {FilterKey} from 'search/SearchConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import OnDemandLoginSection from './OnDemandLoginSection';
import {getLoginParams, PendingRetrieval, searchForRetrievedFile} from './onDemandRetrieval';

interface BrowseFilesRetrievalPanelProps {
  connectionId: string;
  instrumentServiceDetails: InstrumentServiceDetails;
  runApiId: string;
  isMulti: boolean;
  // How many files the user can select here, or null for no limit.
  maxSelectableCount: number | null;
  onPendingRetrievalChange: (pendingRetrieval: PendingRetrieval | null) => void;
}

function notifyBrowseFilesError(error: unknown): void {
  const isTimeout = error instanceof Error && error.message.includes('Polling failed');
  if (isTimeout) {
    notifications.error('File listing request timed out. Please try again later.');
    return;
  }
  // jQuery XHR rejections include responseJSON with server error details
  const serverMessage = (error as {responseJSON?: {userMessage?: string}})?.responseJSON?.userMessage;
  if (serverMessage) {
    notifications.error(`Failed to retrieve file listing from connection: "${serverMessage}"`);
    return;
  }
  notifications.error(
    'Failed to retrieve file listing from connection. Please contact Benchling support if the issue persists.'
  );
}

/**
 * Lists the files on a browse-files connection (e.g. Chromeleon), including ones not yet retrieved to
 * Benchling. The selected files are retrieved when the user confirms their selection.
 */
export default function BrowseFilesRetrievalPanel({
  connectionId,
  instrumentServiceDetails,
  runApiId,
  isMulti,
  maxSelectableCount,
  onPendingRetrievalChange,
}: BrowseFilesRetrievalPanelProps): JSX.Element {
  const [state, dispatch] = useReducer(importInstrumentDataModalReducer, INITIAL_STATE);
  const [isRetrieving, setIsRetrieving] = useState(false);

  const requiresLogin = connectionRequiresUserLogin(instrumentServiceDetails);
  const isConnectionAvailable = instrumentServiceDetails.status.value === ConnectionStatus.AVAILABLE;

  const isLoggedIn = state.loginQueryStatus === InstrumentQueryStatus.DONE;
  const shouldRefreshListing = state.currentStepKey === StepKey.LOGIN && (!requiresLogin || isLoggedIn);
  const {loginUsername} = state;

  // Refresh the file listing, as the logged-in user when the connection requires a per-user login, before
  // showing the files.
  useEffect(() => {
    if (!shouldRefreshListing) {
      return undefined;
    }
    let cancelled = false;
    createBrowseFilesInstrumentQuery(connectionId, getLoginParams(loginUsername))
      .then(({instrumentQueryId}) => pollInstrumentQueryStatus(instrumentQueryId))
      .then((query) => {
        if (!cancelled && query.status !== InstrumentQueryStatus.DONE) {
          const detail = query.info || 'An unknown error occurred';
          notifications.error(`Failed to retrieve file listing from connection: "${detail}"`);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          notifyBrowseFilesError(error);
        }
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        // Show the files listed by earlier requests even if this one failed.
        dispatch({
          type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
          nextStepKey: StepKey.PROCESS,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [connectionId, shouldRefreshListing, loginUsername]);

  useEffect(() => {
    if (state.selectedRowIndices.size === 0) {
      onPendingRetrievalChange(null);
      return;
    }
    onPendingRetrievalChange({
      retrieve: async (): Promise<Array<string>> => {
        setIsRetrieving(true);
        try {
          logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT);
          const fileNames = await extractFileNames(state, {runApiId});
          logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT);
          // extractFileNames notifies the user of each file it couldn't retrieve.
          if (fileNames.length < state.selectedRowIndices.size) {
            throw new Error('Some selected files could not be retrieved. Deselect them or try again.');
          }
          const files = await Promise.all(
            fileNames.map((fileName) => searchForRetrievedFile(fileName, connectionId))
          );
          return files.map((file) => file.id);
        } finally {
          setIsRetrieving(false);
        }
      },
    });
  }, [state, runApiId, connectionId, onPendingRetrievalChange]);

  useEffect(
    () => () => {
      onPendingRetrievalChange(null);
    },
    [onPendingRetrievalChange]
  );

  const setInstrumentData = useCallback((instrumentData: Array<InstrumentData>): void => {
    dispatch({type: ImportInstrumentDataModalActionType.SET_INSTRUMENT_DATA, instrumentData});
  }, []);

  // "Select all" selects each row in turn before the table re-renders, so count the selection here rather than
  // reading it from state, which would let it select past maxSelectableCount.
  const selectedCountRef = useRef(state.selectedRowIndices.size);
  useEffect(() => {
    selectedCountRef.current = state.selectedRowIndices.size;
  }, [state.selectedRowIndices]);

  const handleSelectRow = (rowIndex: number): void => {
    const isSelected = state.selectedRowIndices.has(rowIndex);
    const canSelectMore = maxSelectableCount == null || selectedCountRef.current < maxSelectableCount;
    if (
      !isUnselectableFileStatus(state.instrumentData[rowIndex].status, isConnectionAvailable) &&
      (isSelected || canSelectMore)
    ) {
      selectedCountRef.current += isSelected ? -1 : 1;
      dispatch({type: ImportInstrumentDataModalActionType.SELECT_ROW, selectedRowIndex: rowIndex});
    }
  };

  if (state.currentStepKey === StepKey.LOGIN) {
    if (!shouldRefreshListing) {
      return <OnDemandLoginSection connectionId={connectionId} state={state} dispatch={dispatch} />;
    }
    return (
      <Stack spacing="xs">
        <Text size="sm" color="dimmed">
          Getting the latest files from the connection...
        </Text>
        <CenteredHorizontalThrobber />
      </Stack>
    );
  }

  return (
    <Stack spacing="sm">
      {!isConnectionAvailable && (
        <Alert variant="warning">
          {instrumentServiceDetails.status.value === ConnectionStatus.DISABLED
            ? 'This connection is disabled. Please re-enable it to retrieve the latest data.'
            : 'This connection is not currently available. Please try again later for the latest data.'}
        </Alert>
      )}
      {isRetrieving && (
        <Text size="sm" color="dimmed">
          Retrieving the selected files. This can take a couple of minutes.
        </Text>
      )}
      <InstrumentDataTable
        instrumentData={state.instrumentData}
        setInstrumentData={setInstrumentData}
        selectedRowIndices={state.selectedRowIndices}
        onSelectRow={handleSelectRow}
        allowMultiple={isMulti}
        columns={['File name', 'Status', 'Modified at', 'Refetch']}
        filterOptions={[]}
        initialFilterValues={{[FilterKey.CONNECTION]: [connectionId]}}
        isImporting={isRetrieving}
        isConnectionAvailable={isConnectionAvailable}
        loginUsername={state.loginUsername}
        run={{runApiId}}
      />
    </Stack>
  );
}
